import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Board from "react-trello";
import { useLocation, useNavigate } from "react-router-dom";
import { IoArrowUpOutline, IoArrowDownOutline, IoEllipsisVertical } from "react-icons/io5";
import Card from "./kanban_card";
import api from "../../../../../services/api";
import Modal from "../../../../../components/modal";

export default ({ notions, setNotions, projectId, getNotions }) => {
  const KANBAN_LANES = ["TODO", "READY_TO_DEV", "IN_PROGRESS", "TESTING", "DONE", "ARCHIVED"];
  const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = useState(false);

  const { search } = useLocation();
  const navigate = useNavigate();

  const [KanbanLanesSort, setKanbanLanesSort] = useState({
    TODO: "priority",
    READY_TO_DEV: "priority",
    IN_PROGRESS: "priority",
    TESTING: "priority",
    DONE: "priority",
    ARCHIVED: "priority",
  });

  const KANBAN_LANES_COLORS = {
    TODO: "#e0f2fe",
    READY_TO_DEV: "#fff0f6",
    IN_PROGRESS: "#e0f2fe",
    TESTING: "#f3e8ff",
    DONE: "#dcfce7",
    ARCHIVED: "#f3f4f6",
  };

  const StatusTag = ({ status, count }) => {
    if (!status) return null;
    const colors = {
      TODO: "text-xs text-blue-600",
      READY_TO_DEV: "text-xs text-pink-800",
      IN_PROGRESS: "text-xs text-blue-800",
      TESTING: "text-xs text-purple-800",
      DONE: "text-xs text-green-800",
      ARCHIVED: "text-xs text-gray-800",
    };
    return (
      <div className={`px-2 py-1 rounded w-fit ${colors[status]}`}>
        {status} ({count})
      </div>
    );
  };

  return (
    <div className="w-full border border-gray-200 rounded overflow-hidden">
      <style> {`.smooth-dnd-container.vertical { min-height: 70vh !important; }`}</style>
      <CreateTicket
        isOpen={isCreateTicketModalOpen}
        onClose={() => setIsCreateTicketModalOpen(false)}
        projectId={projectId}
        onTicketCreated={(n) => {
          const query = new URLSearchParams(search);
          query.set("task_modal_id", n._id);
          navigate({ search: query.toString() });
          setNotions([n, ...notions]);
          setIsCreateTicketModalOpen(false);
        }}
      />
      {notions && (
        <Board
          components={{ Card }}
          style={{ backgroundColor: "white", overflow: "scroll", height: "100%", width: "100%" }}
          laneStyle={{ overflow: "scroll", width: "100%", height: "100%" }}
          hideCardDeleteIcon
          data={{
            lanes: KANBAN_LANES.map((status) => ({
              id: status,
              label: (
                <div className="flex items-center justify-end gap-2">
                  <button className="text-xs text-blue-600 hover:text-blue-800" onClick={() => setIsCreateTicketModalOpen(true)}>
                    + New ticket
                  </button>
                  <MultiAction
                    actions={[
                      {
                        name: (
                          <span className="flex items-center gap-2">
                            <IoArrowUpOutline className="w-4 h-4" />
                            Priority ASC
                          </span>
                        ),
                        cb: () => {
                          setKanbanLanesSort((prev) => ({ ...prev, [status]: "priority" }));
                        },
                      },
                      {
                        name: (
                          <span className="flex items-center gap-2">
                            <IoArrowDownOutline className="w-4 h-4" />
                            Priority DESC
                          </span>
                        ),
                        cb: () => {
                          setKanbanLanesSort((prev) => ({ ...prev, [status]: "-priority" }));
                        },
                      },
                      {
                        name: (
                          <span className="flex items-center gap-2">
                            <IoArrowUpOutline className="w-4 h-4" />
                            Oldest first (created at)
                          </span>
                        ),
                        cb: () => {
                          setKanbanLanesSort((prev) => ({ ...prev, [status]: "created_at" }));
                        },
                      },
                      {
                        name: (
                          <span className="flex items-center gap-2">
                            <IoArrowDownOutline className="w-4 h-4" />
                            Newest first (created at)
                          </span>
                        ),
                        cb: () => {
                          setKanbanLanesSort((prev) => ({ ...prev, [status]: "-created_at" }));
                        },
                      },
                      {
                        name: (
                          <span className="flex items-center gap-2">
                            <IoArrowUpOutline className="w-4 h-4" />
                            Oldest first (updated at)
                          </span>
                        ),
                        cb: () => {
                          setKanbanLanesSort((prev) => ({ ...prev, [status]: "updated_at" }));
                        },
                      },
                      {
                        name: (
                          <span className="flex items-center gap-2">
                            <IoArrowDownOutline className="w-4 h-4" />
                            Newest first (updated at)
                          </span>
                        ),
                        cb: () => {
                          setKanbanLanesSort((prev) => ({ ...prev, [status]: "-updated_at" }));
                        },
                      },
                    ]}
                  />
                </div>
              ),
              title: (
                <div className="flex justify-between items-center gap-2 w-full">
                  <div className="flex items-center gap-2">
                    <StatusTag status={status} count={notions.filter((t) => t.status === status).length} />
                  </div>
                </div>
              ),
              cards: notions
                .filter((t) => t.status === status)
                .sort((a, b) => {
                  const sort = KanbanLanesSort[status];
                  const order = sort.startsWith("-") ? -1 : 1;
                  const sortKey = sort.replace("-", "");

                  // Put empty/null values at the end
                  if (!a[sortKey] && !b[sortKey]) return 0;
                  if (!a[sortKey]) return 1;
                  if (!b[sortKey]) return -1;

                  // Handle numeric values (like priority)
                  if (typeof a[sortKey] === "number" && typeof b[sortKey] === "number") {
                    return order * (a[sortKey] - b[sortKey]);
                  }

                  // Handle string values
                  return order * String(a[sortKey]).localeCompare(String(b[sortKey]));
                })
                .map((n) => ({
                  notion: n,
                  id: n._id,
                })),
              style: { backgroundColor: KANBAN_LANES_COLORS[status], minHeight: "28rem" },
            })),
          }}
          onCardMoveAcrossLanes={async (fromLaneId, toLaneId, cardId) => {
            const { data, ok } = await api.put(`/notion/${cardId}`, { status: toLaneId });
            if (!ok) return toast.error("Something went wrong");
            setNotions((prev) => prev.map((n) => (n._id === cardId ? data : n)));
            toast.success("Task moved successfully");
          }}
        />
      )}
    </div>
  );
};

const MultiAction = ({ actions }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative h-[24px]">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="inline-flex justify-between items-center h-[24px] gap-4 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50">
        <IoEllipsisVertical className="w-4 h-4" />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full right-0 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-10 overflow-hidden animate-fadeIn">
            <div className="py-2">
              {actions.map((action, index) => (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    action.cb(e);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200 cursor-pointer flex items-center gap-2">
                  {action.name}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
const CreateTicket = ({ isOpen, onClose, projectId, onTicketCreated }) => {
  const [loading, setLoading] = useState(false);

  const [ticket, setTicket] = useState({ description: "", name: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTicket((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...ticket, project_id: projectId };

      const { data, ok } = await api.post("/notion", payload);
      if (!ok) throw new Error("Failed to create ticket");
      onTicketCreated(data);
      setTicket({ description: "", name: "" });
      toast.success("Ticket created successfully");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full md:w-[400px] lg:w-1/4">
      <form onSubmit={handleSubmit} className="space-y-4 p-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Name (optional)
          </label>
          <input
            type="text"
            name="name"
            id="name"
            value={ticket.name}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Ticket name..."
          />
        </div>
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            name="description"
            id="description"
            value={ticket.description}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            rows={4}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Describe the ticket..."
          />
        </div>
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50">
            {loading ? "Creating..." : "Create Ticket"}
          </button>
        </div>
      </form>
    </Modal>
  );
};
